import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { pushSheet, popSheet } from '../sheetStack.js'
import { useIsDesktop } from '../useIsDesktop.js'

const PAD_ANIM_MS = 200

// Read-only integer input that opens a bottom-sheet numpad on tap (mobile),
// or a plain native number input (desktop). Same UX as AmountInput but
// digits-only (no dot / no neg / no calc).
export function IntegerInput(props) {
  const isDesktop = useIsDesktop()
  return isDesktop ? <Desktop {...props} /> : <Mobile {...props} />
}

function Desktop({ value, onChange, min, className = '', invalid = false, ariaLabel }) {
  const cls = `${className}${invalid ? ' is-invalid' : ''}`
  return (
    <input
      type="number"
      inputMode="numeric"
      min={min}
      step="1"
      className={cls}
      value={value}
      onChange={e => onChange(e.target.value.replace(/\D/g, ''))}
      aria-label={ariaLabel}
      aria-invalid={invalid || undefined}
    />
  )
}

function Mobile({ value, onChange, className = '', invalid = false, ariaLabel }) {
  const [padOpen, setPadOpen]       = useState(false)
  const [padMounted, setPadMounted] = useState(false)
  // First key after open replaces the value (calc-style).
  const freshRef = useRef(true)
  useEffect(() => { if (padOpen) freshRef.current = true }, [padOpen])

  useEffect(() => {
    if (padOpen) { setPadMounted(true); return }
    if (padMounted) {
      const t = setTimeout(() => setPadMounted(false), PAD_ANIM_MS)
      return () => clearTimeout(t)
    }
  }, [padOpen, padMounted])

  useEffect(() => {
    if (!padMounted) return
    pushSheet()
    return () => popSheet()
  }, [padMounted])

  // Close on tap outside the numpad (like a real keyboard would).
  useEffect(() => {
    if (!padOpen) return
    function onPointerDown(e) {
      const np = document.querySelector('.numpad')
      if (np && np.contains(e.target)) return
      setPadOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown, { passive: true })
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
    }
  }, [padOpen])

  function press(key) {
    if (key === 'BACK') {
      freshRef.current = false
      onChange(String(value || '').slice(0, -1))
      return
    }
    const cur = String(value || '')
    if (freshRef.current) {
      freshRef.current = false
      onChange(key) // first digit replaces
      return
    }
    if (cur === '0') onChange(key) // no leading zeros
    else             onChange(cur + key)
  }

  return (
    <>
      <input
        type="text"
        inputMode="none"
        readOnly
        className={`${className}${padOpen ? ' is-active' : ''}${invalid ? ' is-invalid' : ''}`}
        value={value}
        onClick={() => setPadOpen(true)}
        onFocus={() => setPadOpen(true)}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
      />
      {padMounted && createPortal(
        <IntPad
          open={padOpen}
          onPress={press}
          onDone={() => setPadOpen(false)}
        />,
        document.body
      )}
    </>
  )
}

function IntPad({ open, onPress, onDone }) {
  const [entered, setEntered] = useState(false)
  useLayoutEffect(() => {
    if (open) {
      const id = requestAnimationFrame(() => setEntered(true))
      return () => cancelAnimationFrame(id)
    }
    setEntered(false)
  }, [open])

  const Btn = ({ k, children, className = '' }) => (
    <button
      type="button"
      className={`np-btn ${className}`}
      onClick={() => (k === 'DONE' ? onDone() : onPress(k))}
    >
      {children ?? k}
    </button>
  )
  return (
    <div className={`numpad numpad-int${entered ? ' numpad-entered' : ''}`}>
      <div className="numpad-grid">
        <Btn k="1" /><Btn k="2" /><Btn k="3" />
        <Btn k="BACK" className="np-fn"><BackIcon /></Btn>

        <Btn k="4" /><Btn k="5" /><Btn k="6" />
        <span className="np-spacer" />

        <Btn k="7" /><Btn k="8" /><Btn k="9" />
        <span className="np-spacer" />

        <span className="np-spacer" /><Btn k="0" /><span className="np-spacer" />
        <Btn k="DONE" className="np-done">DONE</Btn>
      </div>
    </div>
  )
}

function BackIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 5H9l-7 7 7 7h13a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1z" />
      <line x1="18" y1="9" x2="12" y2="15" />
      <line x1="12" y1="9" x2="18" y2="15" />
    </svg>
  )
}
